'use client'

import { useState } from 'react'

import { trackNewsletterSubscription } from '@/lib/c2c/newsletter/track-subscription'

interface NewsletterSubscriptionProps {
    staticBaseUrl: string
    originCity: string
    destinationCity: string
    locale: string
    transportMode: string
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function NewsletterSubscription({
    staticBaseUrl,
    originCity,
    destinationCity,
    locale,
    transportMode,
}: NewsletterSubscriptionProps) {
    const chikuImg = `${staticBaseUrl}static/media/chiku-newsletter.43def637.png`
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
    const [validationError, setValidationError] = useState<string | null>(null)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setValidationError(null)

        if (!EMAIL_RE.test(email)) {
            setValidationError('Please enter a valid email.')
            return
        }

        setStatus('submitting')
        const result = await trackNewsletterSubscription({
            email,
            locale,
            originCity,
            destinationCity,
            transportMode,
        })
        setStatus(result.ok ? 'success' : 'error')
    }

    const isSubscribed = status === 'success'
    const isBusy = status === 'submitting' || isSubscribed

    return (
        <section className="my-[72px] md:my-24" data-section="newsletter" id="newsletter-section">
            <div className="mx-auto flex max-w-[1056px] flex-wrap items-center justify-center gap-8 px-4 md:flex-row-reverse md:flex-nowrap md:justify-between md:gap-4 lg:gap-12">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                    alt="Wanderu mascot"
                    className="w-[400px]"
                    height={347}
                    loading="lazy"
                    src={chikuImg}
                    width={400}
                />

                <form
                    className="flex w-full flex-col gap-8 md:max-w-[620px]"
                    onSubmit={(e) => void handleSubmit(e)}
                    noValidate
                >
                    <h2 className="text-2xl font-bold text-[#00606b] md:text-[32px] md:leading-[40px]">
                        Wander into savings:
                        <br />
                        Get deals in your inbox!
                    </h2>

                    <p className="text-[#2e3033]">
                        Sign up to get the latest deals on bus &amp; train tickets sent directly to
                        you.
                    </p>

                    <div>
                        <input
                            aria-invalid={validationError ? 'true' : undefined}
                            className="w-full rounded-[4px] border border-[#bbbbbb] bg-white px-4 py-3"
                            disabled={isBusy}
                            name="emailAddress"
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email"
                            type="email"
                            value={email}
                        />
                        {validationError && (
                            <p className="mt-2 text-sm text-red-600" role="alert">
                                {validationError}
                            </p>
                        )}
                    </div>

                    <div className="flex items-center gap-6">
                        <button
                            className="rounded-[7px] border-2 border-transparent bg-[#007380] px-6 py-[14px] text-sm leading-[20px] font-semibold tracking-[2px] text-white uppercase hover:bg-[#009aab] disabled:opacity-50"
                            disabled={isBusy}
                            type="submit"
                        >
                            Subscribe
                        </button>
                        {status === 'success' && (
                            <span className="text-sm font-bold text-[#007380]">
                                You&apos;re subscribed!
                            </span>
                        )}
                        {status === 'error' && (
                            <span className="text-sm font-bold text-red-600" role="alert">
                                Something went wrong. Please try again.
                            </span>
                        )}
                    </div>
                </form>
            </div>
        </section>
    )
}
